/**
 * @file src/components/ErrorBoundary.jsx
 * @description Error boundary component for graceful error handling
 * 
 * Catches JavaScript errors anywhere in the child component tree.
 * Displays error message to user and logs to error tracking service.
 * Prevents entire app from crashing when component has error.
 * 
 * Features:
 * - Catches render and lifecycle errors
 * - Shows user-friendly error page
 * - Reports to error tracking service (Sentry/Rollbar)
 * - Allows restart button
 * 
 * Limitations: Does NOT catch:
 * - Event handler errors (use try/catch in handlers)
 * - Async code errors (use .catch() or async/await try/catch)
 * - Server-side rendering errors
 * 
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to protect
 * @returns {React.ReactElement}
 * 
 * @example
 *   <ErrorBoundary>
 *     <CapsuleDetail />
 *   </ErrorBoundary>
 */

import React from 'react';
import { reportError } from '../services/errorReporting';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    reportError(error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-vault-50 px-4">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5 text-2xl">⚠️</div>
            <h1 className="text-xl font-bold text-vault-900 mb-2">Something went wrong</h1>
            <p className="text-sm text-vault-500 mb-6">
              An unexpected error occurred. Our team has been notified. Please refresh the page to try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-accent-500 hover:bg-accent-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
